/**
 * API Key Service
 * Handles API key management for client admins
 */

import { API_ENDPOINTS } from '../config/api';
import { makeAuthenticatedRequest, createApiError } from './apiService';

export interface ApiKey {
  id: string;
  name: string;
  keyPreview: string; // Preview (e.g., "aas_sk_live_...abc12345")
  createdAt: string;
  lastUsedAt?: string;
  revokedAt?: string;
  isActive: boolean;
}

export interface CreateApiKeyRequest {
  name: string;
}

export interface CreateApiKeyResponse {
  apiKey: string; // Full API key (shown only once!)
  name: string;
  createdAt: string;
}

/**
 * List all API keys for the client
 */
export const listApiKeys = async (): Promise<ApiKey[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.API_KEYS.LIST);

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Create new API key
 */
export const createApiKey = async (data: CreateApiKeyRequest): Promise<CreateApiKeyResponse> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.API_KEYS.CREATE, {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Revoke API key
 */
export const revokeApiKey = async (keyId: string): Promise<void> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.API_KEYS.REVOKE(keyId), {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }
};
